// Educational Content for ISO 42001

export const educationalContent = {
  whatIsISO42001: {
    title: "What is ISO 42001?",
    description: "ISO/IEC 42001:2023 is the world's first international standard for AI Management Systems (AIMS).",
    sections: [
      {
        heading: "Overview",
        content: "ISO 42001 provides a framework for organizations to develop, deploy, and manage AI systems responsibly. It addresses governance, risk management, and compliance requirements specific to AI technologies."
      },
      {
        heading: "Why It Matters",
        content: "As AI becomes integral to business operations, ISO 42001 helps organizations build trust, ensure ethical AI use, manage risks, and demonstrate commitment to responsible AI governance."
      },
      {
        heading: "Key Benefits",
        points: [
          "Structured approach to AI governance",
          "Risk mitigation and compliance",
          "Enhanced stakeholder trust",
          "Competitive advantage",
          "Alignment with global regulations"
        ]
      }
    ],
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ" // Placeholder
  },
  
  frameworkComparison: {
    title: "ISO 42001 vs Other AI Frameworks",
    frameworks: [
      {
        name: "ISO 42001",
        type: "International Standard",
        scope: "Comprehensive AI Management System",
        certification: "Yes - Third-party certification available",
        strengths: [
          "Holistic AI governance framework",
          "Internationally recognized",
          "Certification provides credibility",
          "Covers entire AI lifecycle"
        ],
        bestFor: "Organizations seeking formal certification and comprehensive AI governance"
      },
      {
        name: "NIST AI Risk Management Framework",
        type: "Voluntary Framework",
        scope: "AI Risk Management",
        certification: "No",
        strengths: [
          "Risk-focused approach",
          "Flexible implementation",
          "Free and accessible",
          "US government backing"
        ],
        bestFor: "US-based organizations prioritizing risk management without certification needs"
      },
      {
        name: "EU AI Act",
        type: "Legal Regulation",
        scope: "AI Safety and Compliance",
        certification: "Required for high-risk AI",
        strengths: [
          "Legal compliance requirement",
          "Risk-based classification",
          "Consumer protection focus",
          "Market access in EU"
        ],
        bestFor: "Organizations operating in or selling to the European Union"
      },
      {
        name: "IEEE 7000 Series",
        type: "Technical Standards",
        scope: "Ethical AI Design",
        certification: "Limited",
        strengths: [
          "Ethics-focused",
          "Technical specifications",
          "Engineering perspective",
          "Values-based approach"
        ],
        bestFor: "AI developers and engineers focused on ethical design principles"
      }
    ]
  },
  
  commonMisconceptions: [
    {
      myth: "ISO 42001 is only for large tech companies",
      reality: "ISO 42001 is scalable and applicable to organizations of all sizes that develop, deploy, or manage AI systems."
    },
    {
      myth: "You need to be an AI expert to implement ISO 42001",
      reality: "While technical knowledge helps, ISO 42001 focuses on management systems. With proper guidance, any organization can implement it."
    },
    {
      myth: "ISO 42001 certification guarantees AI safety",
      reality: "Certification demonstrates commitment to AI governance best practices, but ongoing vigilance and continuous improvement are essential."
    },
    {
      myth: "ISO 42001 replaces the EU AI Act",
      reality: "ISO 42001 complements regulations like the EU AI Act. Certification can help demonstrate compliance but doesn't replace legal obligations."
    },
    {
      myth: "Implementation takes years and costs millions",
      reality: "Timeline and costs vary by organization size and AI maturity. With expert guidance, small-to-mid organizations can achieve certification in 6-12 months."
    }
  ],
  
  downloadableResources: [
    {
      id: 'checklist',
      title: "ISO 42001 Readiness Checklist",
      description: "Quick assessment tool to evaluate your current AI governance maturity",
      type: "PDF",
      icon: "FileText"
    },
    {
      id: 'policy-template',
      title: "AI Policy Template",
      description: "Starter template for developing your organization's AI governance policy",
      type: "PDF",
      icon: "Shield"
    },
    {
      id: 'implementation-guide',
      title: "Implementation Roadmap Guide",
      description: "Step-by-step guide to planning your ISO 42001 implementation journey",
      type: "PDF",
      icon: "TrendingUp"
    },
    {
      id: 'risk-register',
      title: "AI Risk Register Template",
      description: "Template for documenting and managing AI-related risks",
      type: "PDF",
      icon: "AlertTriangle"
    }
  ]
};

export default educationalContent;
